/* eslint-disable no-console */
import { IdToken } from '@auth0/auth0-react';
import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  processInitialDataEstadoOk,
  shouldHideFooter,
  shouldShowMinimalFooter,
} from './AuthenticatedNativo';
import ROUTES from './routes';

import { Footer, FooterComplex, Splash } from '@/components';
import useAuth from '@/hooks/useAuth';
import useCountMedals from '@/hooks/useCountMedals';
import usePinsAccount from '@/hooks/usePinsAccount';
import useToast from '@/hooks/useToast';
import { useTournament } from '@/hooks/useTournament';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import { getAccountInitialData } from '@/services';
import { rememberUser } from '@/services/editProfile_pwdReset';
import { TEXTS } from '@/utils';
import {
  removeAxiosTokenInterceptor,
  setupAxiosTokenInterceptor,
} from '@/utils/axiosGdt';

/**
 * Authenticated
 * <br>
 * Explicado en useAuth.
 * <quote> auth0 (..)</quote>
 * @see {useAuth}
 */
const Authenticated = () => {
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { show } = useToast();
  const { handleSetTournamentMaps } = useTournament();
  const { handleSetPinsData, pinsData } = usePinsAccount();
  const { handleSetMedalsData } = useCountMedals();

  const {
    isLoading: auth0Loading,
    isAuthenticated,
    getIdTokenClaims,
    logout,
    setUserInitialData,
    setLocalStorageUser0,
    removeLocalStorageUser0,
    user: auth0User,
    getAccessTokenSilently,
  } = useAuth();

  const { setTshirtAndShield } = useTshirtAndShield();
  const location = useLocation();

  const handleRedirectAfterLogin = useCallback(async () => {
    setUserInitialData(null);

    try {
      if (isAuthenticated) {
        setLocalStorageUser0(JSON.stringify(auth0User));

        // Renuevo credenciales
        const claims: IdToken | undefined = await renewCredentials(
          getAccessTokenSilently,
          getIdTokenClaims
        );

        if (!claims || !claims.__raw || !claims.exp) {
          log('log', 'No claims found');
          show(TEXTS.defaultError);
          return;
        }

        if (claims.exp * 1000 < Date.now()) {
          console.warn('Token expired (2)');
          logout();
          return;
        }

        const result = await getAccountInitialData({
          idToken: claims.__raw,
        });

        if (result && result.estado) {
          setupAxiosTokenInterceptor(getAccessTokenSilently);
          rememberUser({ email: claims.email, sub: claims.sub });
          await processInitialDataEstadoOk(
            result,
            setUserInitialData,
            setTshirtAndShield,
            handleSetTournamentMaps,
            handleSetPinsData,
            handleSetMedalsData,
            navigate,
            location
          );
          setLoading(false);
        } else {
          setUserInitialData(null);

          if (result) {
            switch (result.codError) {
              case 7:
              case 14:
              case 18:
              case 16:
              case undefined: {
                show(result.mensaje);
                console.log('1');
                logout();
                break;
              }

              case 10: {
                if (location.pathname === '/preguntas-frecuentes') {
                  navigate(ROUTES.preguntasFrecuentes, {
                    replace: true,
                  });
                  setLoading(false);
                } else {
                  navigate(ROUTES.registration, {
                    replace: true,
                  });
                  setLoading(false);
                }
                break;
              }

              default: {
                console.log('2.1');
                console.log(result.codError);
                show(TEXTS.defaultError);
                logout();
              }
            }
          } else {
            console.log('3');
            show(TEXTS.defaultError);
            logout();
          }
        }
      } else {
        removeAxiosTokenInterceptor();
        removeLocalStorageUser0();
        setLoading(false);
      }
    } catch (error) {
      const err = error as Error;
      show(err.message);
      if (err.message.includes('Consent required')) {
        // http://localhost no es verificable, auth0 no permite el silent auth.
        // cambio de 2025
        console.warn('solo dev? Consent para http://localhost');
        logout();
        return;
      }
      navigate(ROUTES.notFound, {
        replace: true,
      });
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!auth0Loading) {
      handleRedirectAfterLogin();
    }
  }, [auth0Loading, handleRedirectAfterLogin]);

  if (isLoading || auth0Loading) {
    return <Splash />;
  }

  // oculto el footer para urls determinadas
  const showMinimalFooter =
    shouldShowMinimalFooter(window.location.pathname) ||
    (!isAuthenticated &&
      window.location.pathname === ROUTES.preguntasFrecuentes);

  // oculto el footer para urls determinadas
  const hideFooter = shouldHideFooter(window.location.pathname);

  return (
    <>
      <Outlet />
      {!hideFooter &&
        (showMinimalFooter ? (
          <Footer />
        ) : (
          <FooterComplex isPremium={pinsData.isPremium} />
        ))}
    </>
  );
};

const ENABLE_LOGGING =
  localStorage.getItem('enableAuthenticatedLogging') === 'true';

function log(level: 'error' | 'warn' | 'log' | 'debug', ...args: any[]) {
  if (/* process.env.NODE_ENV === 'development' || */ ENABLE_LOGGING) {
    console[level](...args);
  }
}

/**
 * Lee el token, si está expirado lo renueva
 * (deshabilitando la caché local, porque en expiraciones absurdamente bajas es necesario).
 */
async function renewCredentials(
  getAccessTokenSilently: (arg0: {
    detailedResponse: boolean;
    cacheMode: 'on' | 'off';
  }) => any,
  getIdTokenClaims: () => IdToken | PromiseLike<IdToken | undefined> | undefined
) {
  let accessToken = await getAccessTokenSilently({
    detailedResponse: true,
    cacheMode: 'on',
  }); // retry
  log('debug', 'token renewed(1):' + JSON.stringify(accessToken));
  let claims: IdToken | undefined = await getIdTokenClaims();

  if (!claims || !claims.__raw || !claims.exp) {
    log('log', 'No claims found');
    return claims;
  }

  log('log', 'idToken exp=' + new Date(claims.exp * 1000));
  if (claims.exp * 1000 < Date.now()) {
    log('log', 'Token expired (1)');
    accessToken = await getAccessTokenSilently({
      detailedResponse: true,
      cacheMode: 'off',
    }); // retry
    log('debug', 'token renewed:' + JSON.stringify(accessToken));
    claims = await getIdTokenClaims();
    if (!claims || !claims.__raw || !claims.exp) {
      log('debug', 'No claims found (2)');
      return claims;
    }
    log('debug', 'id Token.exp=' + new Date(claims.exp * 1000));
    if (claims.exp * 1000 < Date.now()) {
      log('debug', 'Token expired (2)');
    } else {
      log('debug', 'Token renewed (2)');
    }
  }
  return claims;
}

export default Authenticated;
